import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ToastrService} from "ngx-toastr";
import {ActivatedRoute, Router} from "@angular/router";
import {TokenStorageService} from "../../services/storage/token-storage.service";
import {UserService} from "../../services/user.service";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
@Component({
    selector: 'app-generate-presigned-url',
    templateUrl: './generate-presigned-url.component.html',
    styleUrl: './generate-presigned-url.component.css',
    standalone: false
})
export class GeneratePresignedUrlComponent implements OnInit {
  form: FormGroup = new FormGroup({});
  submitted: boolean = false;
  loading: boolean = false;
  user: any;
  env: any;
  preSignedUrl: string = '';
  tradeBaseUrl: string = '';
  marketDataBaseUrl: string = '';
  error: string = '';
  copiedField: string | null = null;
  environments: string[] = ['dev', 'qa', 'sandbox', 'uat', 'prod'];
  regions: string[] = ['asia', 'mena', 'europe', 'us', 'hk'];
  isExistingAppName: boolean = false;
  override: boolean = false;
  generateDisabled: boolean = false;

  emailTemplate: string = `
  <div style="font-family: 'Open Sans', sans-serif; font-size:14px; color:#202020; max-width:600px; margin:auto; background:#fff; padding:20px; border-radius:8px;">

    <div style="display:flex; flex-direction:column; align-items:flex-end; margin-bottom:20px;">
      <img src="https://developer.gtngroup.com/assets/img/GTN-Logo-Dark.png"
           alt="GTN Logo"
           style="max-width:90px; margin-bottom:10px;">

      <h2 style="margin:0; color:#202020; font-size:20px; text-align:center; width:100%;">
        GTN Embed APIs – Institution Access Key Generation
      </h2>
    </div>

    <p>
      You have been granted access to generate API credentials for your institution under the GTN Embed APIs platform.
    </p>
    <p>
    Please click the link below to securely generate your institution keys:
    </p>

    <div style="display:inline-block; background:#f0f4f8; padding:10px 20px; margin:10px 0; border-radius:5px; font-size:16px; font-weight:bold; color:#1a1a1a;">
      <a href="$preSignedUrl" target="_blank" style="color:#0066cc; text-decoration:underline;">Generate App Keys</a>
    </div>

    <p style="margin-top:15px; font-size:13px; color:#444; text-align:left;">
      Once generated, these credentials will be required to authenticate and access GTN APIs.
    </p>

    <hr style="margin:20px 0; border-color:#eaeaea;">

    <div style="font-size:12px; color:#444; line-height:1.6; text-align:left;">
      <p><strong>Important Notes:</strong></p>
      <ul style="margin:0 0 10px 20px; padding:0;">
        <li>The link provided is secure and will expire 24 hours after being issued.</li>
        <li>Please ensure that your generated Private Key is securely stored and not shared.</li>
      </ul>
    </div>

    <hr style="margin:20px 0; border-color:#eaeaea;">

    <div style="font-size:11px; color:#666; line-height:1.4; text-align:left;">
      <p>This e-mail was sent to you by GTN Group Holding Limited to give you access to generate the required app keys.</p>
      <p>If you are not the intended recipient or believe you have received this e-mail in error, please contact us at
        <a href="mailto:developer@gtngroup.com" style="color:#0066cc; text-decoration:underline;">developer@gtngroup.com</a>.
      </p>
      <p style="margin-top:10px; text-align:center; color:#000;">
        CONTACT<br>
        Address: GTN Group Holding Limited | Unit 102, Level 1, Liberty House | Dubai International Financial Centre | Dubai 507133 | United Arab Emirates
      </p>
      <p style="font-size:10px; color:#999; text-align:left;">
        Email transmission security and error-free delivery cannot be guaranteed. The sender does not accept liability for errors or omissions in this message.
      </p>
    </div>
  </div>
`;



  processedTemplate!: SafeHtml;
  email: string = '';
  password: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private tokenStorage: TokenStorageService,
    private userService: UserService,
    private sanitizer: DomSanitizer

  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.user = this.tokenStorage.getUser();
    this.env = this.tokenStorage.getUserEnv();
  }

  initializeForm(): void {
    const alphaNumSpaceRegex = /^[a-zA-Z0-9 _]+$/;

    this.form = this.formBuilder.group({
      appName: ['', [Validators.required, Validators.pattern(alphaNumSpaceRegex)]],
      narration: ['', [Validators.required, Validators.pattern(alphaNumSpaceRegex)]],
      instCode: ['', [Validators.required]],
      environment: ['', [Validators.required]],
      region: ['', [Validators.required]]
    });

    const regionControl = this.form.get('region');

    regionControl?.disable();

    this.form.get('environment')?.valueChanges.subscribe((env: string) => {
      const lowerEnv = env?.trim().toLowerCase();
      const hiddenEnvs = ['dev', 'qa', 'sandbox'];

      if (!env) {
        regionControl?.disable();
      } else if (hiddenEnvs.includes(lowerEnv)) {
        regionControl?.disable();
        regionControl?.setValue('');
      } else {
        regionControl?.enable();
      }
    });


    this.form.valueChanges.subscribe(() => {
      if (this.preSignedUrl) {
        this.preSignedUrl = '';
        this.copiedField = null;
        this.generateDisabled = false;
      }
    });

  }
  onClickSubmit(): void {
    this.submitted = true;
    this.preSignedUrl = '';
    this.error = '';

    if (this.form.invalid) {
      this.toastr.error('Please enter the required fields', 'Generating Pre-Signed URL Failed');
      return;
    }

    const {appName, narration, instCode, environment, region } = this.form.value;

    this.loading = true;

    this.generateUrl(instCode, environment, region, appName, narration, this.override);
  }

  private generateUrl(instCode: string, environment: string, region: string, appName: string, narration: string, override: boolean) {
    this.userService.generatePreSignedUrl(instCode, environment, region, appName, narration, override).subscribe({
      next: (data) => {
        this.loading = false;

        if (data) {
          this.isExistingAppName = data.isExistingAppName;

          // If app name already exists and override is false, prompt user
          if (this.isExistingAppName && !override) {
            if (confirm('The app name already exists. Do you want to update the existing configs?')) {
              // User clicked yes -> set override to true and call API again
              this.override = true;
              this.generateUrl(instCode, environment, region, appName, narration, this.override);
            } else {
              // User clicked no -> prompt to change app name
              this.toastr.error('Please change the app name and try again.', 'App Name Exists');
            }
          }

          if(data.url){
            this.preSignedUrl = data.url;
            const baseUrl = this.userService['getBaseUrl'](environment.toLowerCase(), region?.toLowerCase());
            this.tradeBaseUrl = baseUrl + "/trade";
            this.marketDataBaseUrl = baseUrl + "/market-data";

            const html = this.emailTemplate
              .replace(/\$preSignedUrl/g, this.preSignedUrl)
              .replace(/\$tradeBaseUrl/g, this.tradeBaseUrl)
              .replace(/\$marketDataBaseUrl/g, this.marketDataBaseUrl);

            this.processedTemplate = this.sanitizer.bypassSecurityTrustHtml(html);

            this.generateDisabled = true;
          }

        } else {
          this.toastr.error('Invalid response from server', 'Error');
        }
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err?.error?.message || 'Something went wrong', 'Generating Pre-Signed URL Failed');
      }
    });
  }


  get formControl(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  copyToClipboard(text: string, field: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedField = field;
      setTimeout(() => {
        this.copiedField = null;
      }, 2000);
    }).catch(() => {
    });
  }

  downloadTemplate() {
    if (!this.processedTemplate) return;
    const htmlContent = this.emailTemplate.replace(/\$preSignedUrl/g, this.preSignedUrl).replace(/\$tradeBaseUrl/g, this.tradeBaseUrl).replace(/\$marketDataBaseUrl/g, this.marketDataBaseUrl);

    const blob = new Blob([htmlContent], { type: 'text/html' });

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'email-template.html';
    a.click();

    URL.revokeObjectURL(a.href);
  }

  openInOutlook() {
    if (!this.processedTemplate) return;

    const htmlContent = this.emailTemplate
      .replace(/\$preSignedUrl/g, this.preSignedUrl)
      .replace(/\$tradeBaseUrl/g, this.tradeBaseUrl)
      .replace(/\$marketDataBaseUrl/g, this.marketDataBaseUrl);

    const toEmail = this.email;
    const fromEmail = 'developer@gtngroup.com';

    const region = this.form.value.region?.trim();
    const env = this.form.value.environment?.trim();
    const instCode = this.form.value.instCode?.trim();

    const regionPart = region ? `${region.toUpperCase()} ` : '';
    const envPart = env ? `${env.toUpperCase()} ` : '';
    const instPart = instCode ? instCode.toUpperCase() : '';

    const subject = `Generate Access Keys for GTN Embed APIs for Institution ${instPart} in ${regionPart}${envPart}`;

    // .eml content
    const emlContent = [
      `From: ${fromEmail}`,
      `To: ${toEmail}`,
      `Subject: ${subject}`,
      `X-Unsent: 1`, // <-- tells Outlook this is a draft
      `Content-Type: text/html; charset="UTF-8"`,
      '',
      htmlContent
    ].join('\r\n');

    const blob = new Blob([emlContent], { type: 'message/rfc822' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'gtn-email-draft.eml'; // Outlook opens this as editable draft
    a.click();

    URL.revokeObjectURL(url);
  }


}

